import * as React from 'react';
import {
  LocalizationProvider,
  DatePicker as MuiDatePicker,
  type DatePickerProps as MuiDatePickerProps,
} from '@mui/x-date-pickers';
import { type TextFieldProps } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(timezone);

export type DatePickerProps = MuiDatePickerProps &
  TextFieldProps & {
    format?: string;
    inputDisabled?: boolean;
    shouldRespectLeadingZeros?: boolean;
    localTime?: boolean;
  };

/**
 * @prop localTime: false by default.
 *
 * If true, the uploaded and downloaded time will be the start of day in local time in UTC.
 * e.g. User at Seattle (UTC-8) selected 2025-02-12T00:00:00. The uploaded time will be 2025-02-12T08:00:00Z.
 *
 * By default, all time will be in UTC+8.
 * e.g. User anywhere selected 2025-02-12T00:00:00. The uploaded time will be 2025-02-11T16:00:00Z. The read
 * time will be 2025-02-12T00:00:00 (or 2025-02-12 in component).
 */
export const DatePicker: React.FunctionComponent<DatePickerProps> = (
  props: DatePickerProps,
) => {
  const {
    value,
    helperText,
    InputLabelProps,
    error = false,
    required,
    name,
    onChange,
    onBlur,
    inputDisabled = false,
    format = 'DD/MM/YYYY',
    size,
    shouldRespectLeadingZeros = false,
    localTime = true,
    ...rest
  } = props;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <MuiDatePicker
        {...rest}
        value={value}
        onChange={(newValue, context) => {
          // TODO: RPG-1572 temp compulsory convert to UTC+8.
          if (newValue && !localTime) {
            const updatedValue = dayjs(newValue)
              .tz('Asia/Hong_Kong')
              .startOf('day');
            onChange?.(updatedValue, context);
          } else {
            onChange?.(newValue, context);
          }
        }}
        format={format}
        {...(!localTime && { timezone: 'Asia/Hong_Kong' })}
        slotProps={{
          popper: {
            sx: {
              zIndex: 1400,
            },

            /**
             * PopperProps is used here to pass modifiers,
             * which are configuration options of Popper.js to control the behavior of the popup box:
             *
             * flip: Allows the popover to flip if necessary to stay within the viewport.
             * preventOverflow: Prevent the pop-up box from overflowing the viewport.
             */

            modifiers: [
              {
                name: 'flip',
                enabled: true,
                options: {
                  altBoundary: true,
                  rootBoundary: 'viewport',
                  padding: 4,
                },
              },
              {
                name: 'preventOverflow',
                enabled: true,
                options: {
                  altAxis: true,
                  altBoundary: true,
                  tether: true,
                  rootBoundary: 'document',
                  padding: 4,
                },
              },
            ],
          },
          textField: {
            InputProps: { readOnly: inputDisabled },
            helperText,
            InputLabelProps,
            error,
            required,
            onBlur,
            size,
            name,
          },
          field: { shouldRespectLeadingZeros },
        }}
      />
    </LocalizationProvider>
  );
};
